/**
 * Client-side Upload Queue with Circuit Breaker
 * 
 * Manages upload concurrency, integrates with IndexedDB persistence,
 * and implements circuit breaker pattern for resilience.
 */

import { uploadWithTus, TusUploadResult } from './tusUploader';
import { 
  saveBlobsToIndexedDB, 
  updateBlobStatus, 
  deleteBlobFromIndexedDB,
  getPendingUpload,
  PendingUpload 
} from './blobStorage';

// Re-export the type
export type { PendingUpload };

// Max concurrent uploads per browser tab
const MAX_CONCURRENT = 2;

// Circuit breaker settings
const CIRCUIT_BREAKER_THRESHOLD = 5; // failures to open circuit
const CIRCUIT_BREAKER_RESET_MS = 30000; // 30 seconds

export type UploadStatus = 'queued' | 'uploading' | 'complete' | 'failed' | 'paused';

export interface QueuedUpload {
  id: string; // IndexedDB record ID
  questionId: string;
  testResponseId: string;
  status: UploadStatus;
  progress: number;
  videoProgress: number;
  audioProgress: number;
  error?: string;
  videoPath?: string;
  audioPath?: string;
  createdAt: number;
}

export interface QueueState {
  uploads: QueuedUpload[];
  activeCount: number;
  queuedCount: number;
  isOnline: boolean;
  isPaused: boolean;
  circuitOpen: boolean;
}

type QueueListener = (state: QueueState) => void;

class UploadQueue {
  private queue: Map<string, QueuedUpload> = new Map();
  private activeUploads = 0;
  private listeners: Set<QueueListener> = new Set();
  private isPaused = false;
  private isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  
  // Circuit breaker state
  private consecutiveFailures = 0;
  private circuitOpen = false;
  private circuitResetTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.isOnline = true;
        this.notifyListeners();
        this.processQueue();
      });
      
      window.addEventListener('offline', () => {
        this.isOnline = false;
        this.notifyListeners();
      });
    }
  }

  /**
   * Enqueue a new upload - saves to IndexedDB first, then queues for upload
   */
  async enqueue(
    questionId: string,
    testResponseId: string,
    videoBlob?: Blob,
    audioBlob?: Blob,
    videoMimeType?: string,
    audioMimeType?: string
  ): Promise<string> {
    // Step 1: Save to IndexedDB IMMEDIATELY (crash-safe)
    const id = await saveBlobsToIndexedDB(
      questionId,
      testResponseId,
      videoBlob,
      audioBlob,
      videoMimeType,
      audioMimeType
    );

    // Step 2: Add to in-memory queue
    const queuedUpload: QueuedUpload = {
      id,
      questionId,
      testResponseId,
      status: 'queued',
      progress: 0,
      videoProgress: 0,
      audioProgress: 0,
      createdAt: Date.now(),
    };

    this.queue.set(id, queuedUpload);
    this.notifyListeners();

    // Step 3: Start processing
    this.processQueue();

    return id;
  }

  /**
   * Re-queue a pending upload from IndexedDB (for recovery)
   */
  async requeueFromIndexedDB(pendingUpload: {
    id: string;
    questionId: string;
    testResponseId: string;
    status: string;
    createdAt: number;
  }): Promise<void> {
    // Don't re-queue if already in queue
    if (this.queue.has(pendingUpload.id)) {
      return;
    }

    const queuedUpload: QueuedUpload = {
      id: pendingUpload.id,
      questionId: pendingUpload.questionId,
      testResponseId: pendingUpload.testResponseId,
      status: pendingUpload.status === 'uploaded' ? 'complete' : 'queued',
      progress: 0,
      videoProgress: 0,
      audioProgress: 0,
      createdAt: pendingUpload.createdAt,
    };

    this.queue.set(pendingUpload.id, queuedUpload);
    this.notifyListeners();
    this.processQueue();
  }

  /**
   * Process the queue - upload next items if capacity available
   */
  private async processQueue(): Promise<void> {
    // Don't process if offline, paused, or circuit is open
    if (!this.isOnline || this.isPaused || this.circuitOpen) {
      return;
    }

    // Don't exceed concurrency limit
    if (this.activeUploads >= MAX_CONCURRENT) {
      return;
    }

    // Find next queued item
    const nextItem = Array.from(this.queue.values()).find(
      item => item.status === 'queued'
    );

    if (!nextItem) {
      return;
    }

    // Start upload
    this.activeUploads++;
    nextItem.status = 'uploading';
    this.notifyListeners();

    try {
      await this.uploadItem(nextItem);
      
      // Success - reset circuit breaker
      this.consecutiveFailures = 0;
      if (this.circuitOpen) {
        this.circuitOpen = false;
        this.notifyListeners();
      }
      
    } catch (error) {
      console.error('[UploadQueue] Upload failed:', error);
      nextItem.status = 'failed';
      nextItem.error = error instanceof Error ? error.message : 'Upload failed';
      
      // Circuit breaker logic
      this.consecutiveFailures++;
      if (this.consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD) {
        this.openCircuit();
      }
    } finally {
      this.activeUploads--;
      this.notifyListeners();
      
      // Process next item
      this.processQueue();
    }
  }

  /**
   * Upload a single item (video + audio)
   */
  private async uploadItem(item: QueuedUpload): Promise<void> {
    // Get blobs from IndexedDB
    const pending = await getPendingUpload(item.id);
    if (!pending) {
      throw new Error('Pending upload not found in IndexedDB');
    }

    await updateBlobStatus(item.id, 'uploading');

    const uploadPromises: Promise<TusUploadResult>[] = [];
    const basePath = `${pending.testResponseId}/`;

    // Upload video if present
    if (pending.videoBlob) {
      const videoExt = this.getExtension(pending.videoMimeType || 'video/webm');
      const videoPath = `${basePath}video_${crypto.randomUUID()}.${videoExt}`;
      
      uploadPromises.push(
        uploadWithTus({
          blob: pending.videoBlob,
          bucketName: 'test-responses',
          filePath: videoPath,
          contentType: pending.videoMimeType || 'video/webm',
          onProgress: (progress) => {
            item.videoProgress = progress;
            item.progress = this.calculateOverallProgress(item);
            this.notifyListeners();
          },
        }).then(result => {
          if (result.success) {
            item.videoPath = result.filePath;
          }
          return result;
        })
      );
    }

    // Upload audio if present
    if (pending.audioBlob) {
      const audioExt = this.getExtension(pending.audioMimeType || 'audio/webm');
      const audioPath = `${basePath}audio_${crypto.randomUUID()}.${audioExt}`;
      
      uploadPromises.push(
        uploadWithTus({
          blob: pending.audioBlob,
          bucketName: 'test-responses',
          filePath: audioPath,
          contentType: pending.audioMimeType || 'audio/webm',
          onProgress: (progress) => {
            item.audioProgress = progress;
            item.progress = this.calculateOverallProgress(item);
            this.notifyListeners();
          },
        }).then(result => {
          if (result.success) {
            item.audioPath = result.filePath;
          }
          return result;
        })
      );
    }

    // Wait for all uploads
    const results = await Promise.all(uploadPromises);
    
    // Check for failures
    const failed = results.find(r => !r.success);
    if (failed) {
      throw failed.error || new Error('Upload failed');
    }

    // Mark as uploaded in IndexedDB
    await updateBlobStatus(item.id, 'uploaded');
    item.status = 'complete';
    item.progress = 100;
  }

  private calculateOverallProgress(item: QueuedUpload): number {
    const videoWeight = item.videoPath !== undefined || item.videoProgress > 0 ? 0.8 : 0;
    const audioWeight = item.audioPath !== undefined || item.audioProgress > 0 ? 0.2 : 0;
    
    if (videoWeight === 0 && audioWeight === 0) return 0;
    
    const normalizedVideoWeight = videoWeight / (videoWeight + audioWeight);
    const normalizedAudioWeight = audioWeight / (videoWeight + audioWeight);
    
    return Math.round(
      item.videoProgress * normalizedVideoWeight +
      item.audioProgress * normalizedAudioWeight
    );
  }

  private getExtension(mimeType: string): string {
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('aac')) return 'aac';
    return 'webm';
  }

  /**
   * Open the circuit breaker
   */
  private openCircuit(): void {
    console.warn('[UploadQueue] Circuit breaker OPEN - too many failures');
    this.circuitOpen = true;
    this.notifyListeners();

    // Auto-reset after timeout
    if (this.circuitResetTimeout) {
      clearTimeout(this.circuitResetTimeout);
    }
    
    this.circuitResetTimeout = setTimeout(() => {
      console.log('[UploadQueue] Circuit breaker RESET');
      this.circuitOpen = false;
      this.consecutiveFailures = 0;
      this.notifyListeners();
      this.processQueue();
    }, CIRCUIT_BREAKER_RESET_MS);
  }

  /**
   * Pause all uploads
   */
  pause(): void {
    this.isPaused = true;
    this.notifyListeners();
  }

  /**
   * Resume uploads
   */
  resume(): void {
    this.isPaused = false;
    this.notifyListeners();
    this.processQueue();
  }

  /**
   * Retry a failed upload
   */
  retry(id: string): void {
    const item = this.queue.get(id);
    if (item && item.status === 'failed') {
      item.status = 'queued';
      item.progress = 0;
      item.videoProgress = 0;
      item.audioProgress = 0;
      item.error = undefined;
      this.notifyListeners();
      this.processQueue();
    }
  }

  /**
   * Mark an upload as fully saved (database confirmed)
   * This triggers IndexedDB cleanup
   */
  async markAsSaved(id: string): Promise<void> {
    await deleteBlobFromIndexedDB(id);
    this.queue.delete(id);
    this.notifyListeners();
  }

  /**
   * Get current upload by ID
   */
  getUpload(id: string): QueuedUpload | undefined {
    return this.queue.get(id);
  }

  /**
   * Get upload by question ID
   */
  getUploadByQuestionId(questionId: string): QueuedUpload | undefined {
    return Array.from(this.queue.values()).find(u => u.questionId === questionId);
  }

  /**
   * Subscribe to queue state changes
   */
  subscribe(listener: QueueListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current state
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  /**
   * Get current queue state
   */
  getState(): QueueState {
    const uploads = Array.from(this.queue.values());
    return {
      uploads,
      activeCount: uploads.filter(u => u.status === 'uploading').length,
      queuedCount: uploads.filter(u => u.status === 'queued').length,
      isOnline: this.isOnline,
      isPaused: this.isPaused,
      circuitOpen: this.circuitOpen,
    };
  }

  /**
   * Check if there are pending uploads
   */
  hasPendingUploads(): boolean {
    return Array.from(this.queue.values()).some(
      u => u.status === 'queued' || u.status === 'uploading'
    );
  }

  /**
   * Get count of pending uploads
   */
  getPendingCount(): number {
    return Array.from(this.queue.values()).filter(
      u => u.status === 'queued' || u.status === 'uploading'
    ).length;
  }

  private notifyListeners(): void {
    const state = this.getState();
    this.listeners.forEach(listener => listener(state));
  }
}

// Singleton instance
export const uploadQueue = new UploadQueue();
